import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ListRefreshIndicator } from '@/features/order-confirmation/components/ListRefreshIndicator'
import { Tooltip } from '@/components/ui/Tooltip'
import { UserAvatar } from '@/components/UserAvatar'
import {
  describeChange,
  formatChangeTime,
  lastEditedBy,
} from '@/features/sales-order/changeDescription'
import { Pagination } from '@/components/ui/Pagination'
import { relativeTime } from '@/lib/relativeTime'
import { cn } from '@/lib/utils'
import { formatAmount, statedTotal } from '@/features/sales-order/amount'
import type { CustomerOrderItem, CustomerOrderList } from '@/types/salesOrder'
import type { CustomerOrderListParams } from '@/services/api'
import { salesOrderQueryOptions } from '@/features/sales-order/salesOrderQueries'
import type { SalesOrderOverviewOutletContext } from '../pages/OverviewPage'

/**
 * Who last touched the order, as their own circle.
 *
 * The hover carries what they did and when. An entry the log cannot describe any better than by
 * who and when says exactly that, rather than dressing up a key nobody outside the code knows.
 */
function LastChange({ item }: { item: CustomerOrderItem }) {
  const change = item.last_change
  if (!change?.by) return null

  const description = describeChange(change)

  return (
    <Tooltip
      content={
        <span className="block">
          <span className="block">{description || lastEditedBy(change)}</span>
          <span className="mt-0.5 block text-gray-300">{formatChangeTime(change.timestamp)}</span>
        </span>
      }
    >
      {/* The circle is the whole target: a span around it keeps the tooltip on the symbol
          rather than on the width of the cell. */}
      <span className="inline-flex">
        <UserAvatar firstName={change.by.first_name} lastName={change.by.last_name} />
      </span>
    </Tooltip>
  )
}

function Row({ item, onClick }: { item: CustomerOrderItem; onClick: () => void }) {
  const total = statedTotal(item)

  return (
    <tr
      className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50"
      onClick={onClick}
    >
      <td className="px-4 py-3">
        {/*
          Until the pipeline has matched an ERP customer, the name is only what the document said.
          The name greys rather than carrying a line of explanation under it: a queue is read by
          scanning down it, and a settled name should be what stands out. Why it is grey is on
          hover, for the one row being looked at.
        */}
        {item.customer_name ? (
          <p
            className={cn(
              'font-medium',
              item.customer_is_identified ? 'text-gray-900' : 'text-gray-400',
            )}
            title={
              item.customer_is_identified
                ? undefined
                : 'Read from the document, not yet matched to a customer in the ERP'
            }
          >
            {item.customer_name}
          </p>
        ) : (
          <p className="font-medium text-gray-400">No customer</p>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {item.customer_order_number}
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
        {relativeTime(item.received)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {item.number_of_rows > 0
          ? `${item.number_of_rows} rows`
          : null}
      </td>
      {/*
        Right aligned and tabular so a column of amounts can be compared down its digits. An order
        the document gave no total for says so, the way the validation page does: a blank cell
        reads as a column that has not loaded rather than a figure we never captured.
      */}
      <td className="px-4 py-3 text-right text-sm">
        {total === null ? (
          <span className="text-gray-400">No total</span>
        ) : (
          <span className="tabular-nums text-gray-700">{formatAmount(total)}</span>
        )}
      </td>
      {/* Empty for an order nobody has touched, which is most of a fresh queue. */}
      <td className="px-4 py-3 text-right">
        <LastChange item={item} />
      </td>
    </tr>
  )
}

/**
 * The body of one overview tab. Both tabs show the same columns off the same shape, and differ only
 * in which endpoint they read and what they say when they are empty.
 */
export function CustomerOrderTable({
  queryKeyPrefix,
  fetchList,
  emptyMessage,
}: {
  queryKeyPrefix: readonly unknown[]
  fetchList: (workflowId: string, params: CustomerOrderListParams) => Promise<CustomerOrderList>
  emptyMessage: string
}) {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { onCustomerOptions } = useOutletContext<SalesOrderOverviewOutletContext>()

  const page = Number(searchParams.get('page')) || 1
  const selectedCustomers = searchParams.getAll('customer')
  const searchQuery = searchParams.get('q') ?? ''

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [...queryKeyPrefix, page, selectedCustomers, searchQuery],
    queryFn: () =>
      fetchList(workflowId!, {
        page,
        customers: selectedCustomers.length > 0 ? selectedCustomers : undefined,
        q: searchQuery || undefined,
      }),
    enabled: !!workflowId,
    placeholderData: keepPreviousData,
    ...salesOrderQueryOptions,
  })

  const customerFilters = data?.customer_filters
  useEffect(() => {
    onCustomerOptions(customerFilters ?? [])
  }, [customerFilters, onCustomerOptions])

  const isBackgroundRefreshing = isFetching && !isLoading && !!data
  const items = data?.data ?? []
  const hasAnyFilter = selectedCustomers.length > 0 || !!searchQuery

  function handlePage(p: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (p > 1) next.set('page', String(p))
        else next.delete('page')
        return next
      },
      { replace: true }
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        Loading…
      </div>
    )
  }

  if (items.length === 0 && !hasAnyFilter) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="relative">
      <ListRefreshIndicator visible={isBackgroundRefreshing} />

      <div className="data-sheet">
      <table className="w-full text-left">
        <thead>
          <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 pb-2 pt-3">Customer</th>
            <th className="px-4 pb-2 pt-3">Customer order number</th>
            <th className="hidden md:table-cell px-4 pb-2 pt-3">Received</th>
            <th className="px-4 pb-2 pt-3">Rows</th>
            <th className="px-4 pb-2 pt-3 text-right">Total</th>
            {/* No heading: the column is one small circle, and a word over it weighs more than
                what it labels. */}
            <th className="px-4 pb-2 pt-3" />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                No results for current filters
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <Row
                key={item.id}
                item={item}
                onClick={() => navigate(`/sales-order/${workflowId}/customer-order/${item.id}`)}
              />
            ))
          )}
        </tbody>
      </table>
      </div>

      {data && (
        <Pagination
          page={data.page}
          totalCount={data.total}
          pageSize={data.page_size}
          onPage={handlePage}
        />
      )}
    </div>
  )
}
